import React, { useRef, useState } from "react";

/**
 * Draggable Before/After comparison slider.
 * Props:
 *   before, after — image URLs (data-URL or http)
 *   height — CSS height for the panel (default 520)
 */
export default function BeforeAfterSlider({ before, after, height = 520 }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const drag = useRef(false);

  const set = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
    setPos(p);
  };

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden rounded-md bg-slate-100 select-none border border-slate-200"
      style={{ height }}
      onMouseMove={(e) => drag.current && set(e.clientX)}
      onMouseUp={() => (drag.current = false)}
      onMouseLeave={() => (drag.current = false)}
      onTouchMove={(e) => set(e.touches[0].clientX)}
      data-testid="before-after"
    >
      {/* After (full) */}
      {after ? (
        <img src={after} alt="after" className="absolute inset-0 w-full h-full object-contain bg-slate-50" draggable={false} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">Output will appear here</div>
      )}

      {/* Before (clipped) */}
      {before && (
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img
            src={before}
            alt="before"
            className="absolute inset-0 h-full object-contain bg-slate-50"
            style={{ width: `${(100 / pos) * 100}%`, maxWidth: "none" }}
            draggable={false}
          />
        </div>
      )}

      {/* Divider */}
      {before && after && (
        <>
          <div
            className="absolute top-0 bottom-0 w-[2px] bg-orange-500 pointer-events-none"
            style={{ left: `${pos}%` }}
          />
          <button
            type="button"
            onMouseDown={(e) => { drag.current = true; set(e.clientX); }}
            onTouchStart={() => (drag.current = true)}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg border-2 border-orange-500 flex items-center justify-center cursor-ew-resize hover:scale-110 transition-transform"
            style={{ left: `${pos}%` }}
            aria-label="Compare"
            data-testid="before-after-handle"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-orange-600">
              <path d="M8 5l-5 7 5 7M16 5l5 7-5 7" />
            </svg>
          </button>
          <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold tracking-wider uppercase">Before</div>
          <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-orange-600 text-white text-[10px] font-semibold tracking-wider uppercase">After</div>
        </>
      )}
    </div>
  );
}
